<template>
  <div class="map-controls">
    <q-btn
      v-for="btn in buttons"
      :key="btn.icon"
      round
      size="md"
      :color="btn.color || defaultColor"
      :text-color="btn.textColor || defaultTextColor"
      :icon="btn.icon"
      :loading="btn.loading"
      @click="btn.action"
      class="control-btn"
    >
      <q-tooltip anchor="center right" self="center left" :offset="[10, 0]">
        {{ btn.tooltip }}
      </q-tooltip> 
    </q-btn>
  </div>
</template>

<script>
import { fromLonLat } from 'ol/proj'
import { Notify } from 'quasar'

export default {
  name: 'MapControls',
  
  props: {
    map: { type: Object, required: true },
    projection: { type: Object, required: true },
    defaultCenter: { type: Array, default: () => [15.9819, 45.8150] },
    defaultZoom: { type: Number, default: 13 }
  },

  emits: ['location-found', 'location-updated', 'location-error', 'tracking-stopped', 'reset-to-home'],

  data() {
    return {
      loadingLocation: false,
      isTracking: false,
      currentCoords: null,
      watchId: null,
      isProgrammaticMove: false,
      isZooming: false
    }
  },

  computed: {
    defaultColor() {
      return this.$q.dark.isActive ? 'dark' : 'white'
    },

    defaultTextColor() {
      return this.$q.dark.isActive ? 'grey-5' : 'grey-7'
    },

    buttons() {
      return [
        {
          icon: 'add',
          tooltip: this.$t('Zoom In'),
          action: () => this.zoom(1)
        },
        {
          icon: 'remove',
          tooltip: this.$t('Zoom Out'),
          action: () => this.zoom(-1)
        },
        {
          icon: this.isTracking ? 'gps_fixed' : 'gps_not_fixed',
          tooltip: this.locationTooltip,
          color: this.isTracking 
            ? (this.$q.dark.isActive ? 'secondary' : 'teal') 
            : this.defaultColor,
          textColor: this.isTracking ? 'white' : this.defaultTextColor,
          loading: this.loadingLocation,
          action: this.toggleTracking
        },
        {
          icon: 'home',
          tooltip: this.$t('Reset to Default View'),
          action: this.resetView
        }
      ]
    },

    locationTooltip() {
      if (this.loadingLocation) return this.$t('Finding your location...')
      if (this.isTracking) return this.$t('Stop location tracking')
      return this.$t('Start location tracking')
    }
  },

  beforeUnmount() {
    this.stopTracking()
  },

  methods: {
    // ===== ZOOM =====
    zoom(delta) {
      const view = this.map?.getView()
      if (!view) return

      const currentZoom = view.getZoom()
      const newZoom = Math.max(1, Math.min(20, currentZoom + delta))

      this.isZooming = true
      view.animate({ zoom: newZoom, duration: 300 })
      setTimeout(() => { this.isZooming = false }, 400)
    },

    // ===== LOCATION TRACKING =====
    async toggleTracking() {
      if (this.isTracking) {
        this.stopTracking()
        this.notify(this.$t('Location tracking stopped'), 'info')
      } else {
        await this.startTracking()
      }
    },

    async startTracking() {
      if (!navigator.geolocation) {
        this.notify(this.$t('Geolocation not supported'), 'negative')
        return
      }

      this.loadingLocation = true

      try {
        const position = await this.getPosition()
        this.currentCoords = [position.coords.longitude, position.coords.latitude]
        this.isTracking = true

        this.animateToLocation(this.currentCoords, true)
        this.startWatching()
        this.addMoveListener()

        this.$emit('location-found', { 
          coordinates: this.currentCoords,
          accuracy: position.coords.accuracy 
        })

        this.notify(
          `${this.$t('Location tracking started')} (±${Math.round(position.coords.accuracy)}m)`,
          'positive'
        )
      } catch (error) {
        this.handleLocationError(error)
      } finally {
        this.loadingLocation = false
      }
    },

    stopTracking() {
      if (this.watchId) {
        navigator.geolocation.clearWatch(this.watchId)
        this.watchId = null
      }
      this.removeMoveListener()
      this.isTracking = false
      this.$emit('tracking-stopped')
    },

    getPosition() {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        })
      })
    },

    startWatching() {
      if (this.watchId) return

      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          this.currentCoords = [position.coords.longitude, position.coords.latitude]
          
          if (this.isTracking && position.coords.accuracy < 100) {
            this.animateToLocation(this.currentCoords)
          }
          
          this.$emit('location-updated', {
            coordinates: this.currentCoords,
            accuracy: position.coords.accuracy
          })
        },
        () => {
          this.notify(this.$t('Location tracking lost'), 'warning')
          this.stopTracking()
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      )
    },

    animateToLocation(coords, includeZoom = false) {
      const view = this.map.getView()
      const center = fromLonLat(coords, this.projection)
      const duration = includeZoom ? 1000 : 500

      this.isProgrammaticMove = true

      const options = { center, duration }
      if (includeZoom) {
        options.zoom = Math.max(view.getZoom(), 15)
      }

      view.animate(options)
      setTimeout(() => { this.isProgrammaticMove = false }, duration + 100)
    },

    // ===== MAP LISTENERS =====
    addMoveListener() {
      if (this.moveListener) return

      this.map.on('wheel', this.handleWheel)
      this.moveListener = this.map.on('moveend', this.handleMoveEnd)
    },

    removeMoveListener() {
      if (this.moveListener) {
        this.map.un('moveend', this.moveListener)
        this.map.un('wheel', this.handleWheel)
        this.moveListener = null
      }
    },

    handleWheel() {
      this.isZooming = true
      setTimeout(() => { this.isZooming = false }, 500)
    },

    handleMoveEnd() {
      if (!this.isTracking || this.isProgrammaticMove || this.isZooming) return
      if (this.isLocationVisible()) return

      this.stopTracking()
      this.notify(this.$t('Location tracking stopped - moved out of view'), 'info')
    },

    isLocationVisible() {
      if (!this.currentCoords) return false

      try {
        const view = this.map.getView()
        const extent = view.calculateExtent(this.map.getSize())
        const [minX, minY, maxX, maxY] = extent
        const [userX, userY] = fromLonLat(this.currentCoords, this.projection)

        // 10% buffer
        const bufferX = (maxX - minX) * 0.1
        const bufferY = (maxY - minY) * 0.1

        return userX >= (minX - bufferX) && userX <= (maxX + bufferX) &&
               userY >= (minY - bufferY) && userY <= (maxY + bufferY)
      } catch {
        return true
      }
    },

    // ===== RESET VIEW =====
    resetView() {
      const view = this.map?.getView()
      if (!view) return

      view.animate({
        center: fromLonLat(this.defaultCenter, this.projection),
        zoom: this.defaultZoom,
        duration: 1000
      })

      this.$emit('reset-to-home')
      this.stopTracking()
      this.notify(this.$t('View reset to default location'), 'info')
    },

    // ===== HELPERS =====
    handleLocationError(error) {
      const messages = {
        1: this.$t('Location access denied'),
        2: this.$t('Location unavailable'),
        3: this.$t('Location request timed out')
      }
      
      this.notify(messages[error.code] || this.$t('Could not get location'), 'negative')
      this.$emit('location-error', error)
      this.isTracking = false
    },

    notify(message, color) {
      Notify.create({
        message,
        color,
        position: 'top',
        timeout: color === 'negative' ? 5000 : 2000
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.map-controls {
  position: absolute;
  bottom: var(--map-ui-bottom);
  left: var(--map-ui-left);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: var(--z-map-ui);
}

.control-btn {
  box-shadow: var(--shadow-md);

  body.body--dark & {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
}

@media (max-width: 48rem), (max-height: 48rem)  {
  .map-controls {
    bottom: var(--map-ui-bottom-mobile);
    left: var(--map-ui-left-mobile);
  }
  
  .control-btn {
    min-height: 2.5rem !important;
    min-width: 2.5rem !important;
  }
}

.control-btn {
  :deep(.q-tooltip) {
    z-index: 9999;
  }
}
</style>